import { ApolloLink, FetchResult, Observable } from "@apollo/client";
import * as Sentry from "@sentry/react";

const getErrorMessage = (error: unknown): string => {
  return error instanceof Error ? error.message : String(error);
};

/**
 * Records GraphQL operations as Sentry breadcrumbs without attaching request
 * variables or response data, which may contain credentials or personal data.
 */
export const createSentryLink = (uri: string): ApolloLink => {
  return new ApolloLink((operation, forward) => {
    const operationName = operation.operationName || "anonymous";
    const variableNames = Object.keys(operation.variables).join(", ");
    const startedAt = Date.now();

    return new Observable<FetchResult>((observer) => {
      let graphqlErrors = "";

      const subscription = forward(operation).subscribe({
        next(result) {
          const errors = "errors" in result ? result.errors : undefined;
          graphqlErrors =
            errors?.map(({ message }) => message).join("; ") || "";
          observer.next(result);
        },
        error(error) {
          Sentry.addBreadcrumb({
            category: "graphql",
            level: "error",
            message: operationName,
            data: {
              durationMs: Date.now() - startedAt,
              error: getErrorMessage(error),
              uri,
              variableNames,
            },
          });
          observer.error(error);
        },
        complete() {
          Sentry.addBreadcrumb({
            category: "graphql",
            level: graphqlErrors ? "error" : "info",
            message: operationName,
            data: {
              durationMs: Date.now() - startedAt,
              errors: graphqlErrors,
              uri,
              variableNames,
            },
          });
          observer.complete();
        },
      });

      return () => subscription.unsubscribe();
    });
  });
};
